// src/pages/viewtemplate.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  closestCenter,
  pointerWithin,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import infoCircle from "../assets/info-circle.svg";

import PageHeader from "../components/PageHeader";
import Help from "../components/Help";
import { helpPagesData } from "../utils/helper";
import trash from "../assets/red-trash.svg";
import textIcon from "../assets/text.svg";
import paragraphIcon from "../assets/paragraph.svg";
import buttonIcon from "../assets/button.svg";
import uploadImageIcon from "../assets/uploadImage.svg";
import tableIcon from "../assets/table.svg";
import urlIcon from "../assets/url.svg";
import subtextIcon from "../assets/subtext.svg";
import videoIcon from "../assets/video.svg";
import emptysection from "../assets/empty-section.svg";
import sortIcon from "../assets/draghandle.svg";
import undoIcon from "../assets/undo.svg";
import dropdownIcon from "../assets/dropdown-icon.svg";
import SwitchToggle from "../components/switch";
import { useToast } from "../components/ToastProvider";

// Actions / API helpers (make sure these are exported from your action file)
import { StandardFieldList, FetchTemplate, UpdateTemplate, DeleteTemplate } from "../action/sectiondata";
import { Spinner } from "react-bootstrap";

/* ---------------------------
   Utilities
----------------------------*/
const uid = () => Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
const tplMapKey = (id) => `tpl-map-${id}`;

// buildPayload: create payload for PUT (update template)
const buildPayload = (templateName, fixedFields, multiFields) => {
  const fixedPayload = fixedFields.map((f) => ({
    label: (f.labelText || f.label).trim(),
    type: f.type,
    required: !!f.required,
    placeholder: f.placeholderEnabled ? (f.placeholder || "") : "",
    standard_field: f.standard_field?._id || null,
  }));

  const multiPayloadFields = multiFields.map((m) => {
    if (m.toggle && m.nested) {
      // nested repeater
      const inner = (m.nestedFields && m.nestedFields[0]) || {};
      return {
        label: (m.labelText || m.label).trim(),
        type: "repeater",
        fields: [
          {
            label: (inner.labelText || inner.label || m.label).trim(),
            type: inner.type || m.originalType || "text",
            required: !!inner.required,
            placeholder: inner.placeholder || (m.placeholderEnabled ? (m.placeholder || "") : ""),
            standard_field:
              typeof inner.standard_field === "string"
                ? inner.standard_field
                : inner.standard_field?._id ||
                (typeof m.standard_field === "string"
                  ? m.standard_field
                  : m.standard_field?._id) ||
                null,

          },
        ],
      };
    }

    // normal multi item (not nested as repeater)
    return {
      label: (m.labelText || m.label).trim(),
      type: m.type,
      required: !!m.required,
      placeholder: m.placeholderEnabled ? (m.placeholder || "") : "",
      standard_field:
        typeof m.standard_field === "string"
          ? m.standard_field
          : m.standard_field?._id || null,
    };
  });

  const payload = {
    label: templateName,
    type: templateName.trim().toLowerCase().replace(/\s+/g, "-"),
    fields: [...fixedPayload],
  };

  if (multiPayloadFields.length > 0) {
    payload.fields.push({
      label: "Cards",
      type: "repeater",
      fields: multiPayloadFields,
    });
  }

  return payload;
};

/* ---------------------------
   Icon fallback map
----------------------------*/
const ICON_MAP = {
  text: textIcon,
  textarea: paragraphIcon,
  button: buttonIcon,
  image: uploadImageIcon,
  table: tableIcon,
  url: urlIcon,
  subtext: subtextIcon,
  video: videoIcon,
};

/* ---------------------------
   Small UI components
----------------------------*/
const EmptyBox = ({ title, desc }) => (
  <div className="empt-state">
    <img src={emptysection} alt="empty" />
    <h6 className="text-center">{title}</h6>
    <p className="text-muted text-center">{desc}</p>
  </div>
);

/* ---------------------------
   Draggable library item
----------------------------*/
function DraggableField({ id, label, icon, payloadType, placeholderEnabled }) {
  const { listeners, setNodeRef, transform, isDragging } = useDraggable({
    id,
    data: { id, label, icon, type: payloadType, source: "library", placeholderEnabled },
  });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    boxShadow: isDragging
      ? "-19px 10px 13px 0px #0000000D, -34px 18px 15px 0px #00000003, -53px 28px 17px 0px #00000000"
      : undefined,
    background: isDragging ? "#fff" : undefined,
    zIndex: isDragging ? 999 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} {...listeners} className="field-item" onDoubleClick={(e) => e.preventDefault()}>
      <span className="field-label">{label}</span>
      {icon && <img src={icon} alt={label} className="draggable-icon" />}
    </div>
  );
}

/* ---------------------------
   Sortable field component (dropped)
----------------------------*/
function SortableField({ item, onLabelChange, onDelete, isExpanded, toggleExpand, onToggleChange, isMulti }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.98 : 1,
    zIndex: isDragging ? 999 : "auto",
    background: isDragging ? "#fff" : undefined,
    boxShadow: isDragging
      ? "-19px 10px 13px 0px #0000000D, -34px 18px 15px 0px #00000003, -53px 28px 17px 0px #00000000"
      : undefined,
  };


  // FIXED: Strong recursive lookup supporting nestedFields + API fields
  const findStandardFieldRecursive = (node) => {
    if (!node || typeof node !== "object") return null;

    // 1) Direct match
    if (node.standard_field) return node.standard_field;

    // 2) UI repeater children (nestedFields)
    if (Array.isArray(node.nestedFields)) {
      for (const child of node.nestedFields) {
        const found = findStandardFieldRecursive(child);
        if (found) return found;
      }
    }

    // 3) API repeater children (fields)
    if (Array.isArray(node.fields)) {
      for (const child of node.fields) {
        const found = findStandardFieldRecursive(child);
        if (found) return found;
      }
    }

    return null;
  };

  return (
    <div ref={setNodeRef} style={style} className="field-name-wrapper" {...attributes}>
      <img ref={setActivatorNodeRef} {...listeners} className="field-sort-btn" src={sortIcon} alt="move" />
      <div className="field-item dropped">
        <div className="dropped-header">
          <div className="dropped-left">
            {(() => {
              const std = findStandardFieldRecursive(item);

              const iconUrl = std?.icon
                ? `${process.env.REACT_APP_BUCKET_URL}${std.icon}`
                : item.icon;

              return (
                <>
                  {iconUrl && <img src={iconUrl} className="dropped-icon" alt="icon" />}
                  <span className="dropped-label">
                    {std?.title || item.label}
                  </span>
                </>
              );

            })()}
          </div>


          <div className="dropped-right">
            <button className="delete-field-btn" onClick={() => onDelete(item.id)} type="button">
              Delete
            </button>

            <button className="expand-btn" onClick={() => toggleExpand(item.id)} type="button">
              <img src={dropdownIcon} alt="toggle" className={`dropdown-arrow ${isExpanded ? "open" : ""}`} />
            </button>
          </div>
        </div>

        {isExpanded && (
          <div className="dropped-body">
            <div className="dropped-fields">
              <input type="text" placeholder="Enter Field label or title" value={item.labelText || ""} onChange={(e) => onLabelChange(item.id, e.target.value, "labelText")} />

              {item.placeholderEnabled && item.placeholder !== null && (
                <input type="text" placeholder="Enter Placeholder" value={item.placeholder || ""} onChange={(e) => onLabelChange(item.id, e.target.value, "placeholder")} />
              )}
              {isMulti && (
                <div className="switch-btn">
                  Enable this if the input will appear multiple times.
                  <SwitchToggle checked={!!item.toggle} onChange={(e) => onToggleChange(item.id, e.target.checked)} />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* -----------------------------------------

  Droppable Fixed container (inside DndContext)

----------------------------------------- */

const DroppableFixed = ({

  fields,

  onLabelChange,

  onDelete,

  expandedSet,

  toggleExpand,

  onToggleChange,

}) => {

  const { setNodeRef, isOver } = useDroppable({ id: "fixed-area" });



  return (

    <div ref={setNodeRef} className={`drop-box ${isOver ? "over" : ""}`}>

      <h6>Fixed Fields</h6>



      {fields.length === 0 ? (

        <EmptyBox

          title="Drop Fixed Fields Here"

          desc="Add single-instance fields like title or subtitle that appear once per section."

        />

      ) : (

        <SortableContext

          items={fields.map((f) => f.id)}

          strategy={verticalListSortingStrategy}

        >

          {fields.map((it) => (

            <SortableField

              key={it.id}

              item={it}

              onLabelChange={onLabelChange}

              onDelete={onDelete}

              isExpanded={expandedSet.has(it.id)}

              toggleExpand={toggleExpand}

              onToggleChange={onToggleChange}

            />

          ))}

        </SortableContext>

      )}

    </div>

  );

};



/* -----------------------------------------

  Droppable Multi container (inside DndContext)

----------------------------------------- */

const DroppableMulti = ({

  fields,

  onLabelChange,

  onDelete,

  expandedSet,

  toggleExpand,

  onToggleChange,

}) => {

  const { setNodeRef, isOver } = useDroppable({ id: "multi-area" });



  return (

    <div ref={setNodeRef} className={`drop-box ${isOver ? "over" : ""}`}>

      <h6>Fields for Multiple Instance</h6>



      {fields.length === 0 ? (

        <EmptyBox

          title="Drop Fields for Multiple Entries"

          desc="Add input fields that generate repeatable sets such as testimonials or FAQs."

        />

      ) : (

        <SortableContext

          items={fields.map((f) => f.id)}

          strategy={verticalListSortingStrategy}

        >

          {fields.map((it) => (

            <SortableField

              key={it.id}

              item={it}

              onLabelChange={onLabelChange}

              onDelete={onDelete}

              isExpanded={expandedSet.has(it.id)}

              toggleExpand={toggleExpand}

              onToggleChange={onToggleChange}

              isMulti={true}

            />

          ))}

        </SortableContext>

      )}

    </div>

  );

};


/* ---------------------------
   Convert API repeater fields into UI items for multi area
----------------------------*/
function convertRepeaterToUI(fields = [], library = []) {
  // fields: array of objects returned inside "Cards" repeater (each could be a field or a nested repeater)
  const out = [];
  for (const child of fields) {
    if (child.type === "repeater") {
      // treat as nested repeater -> show toggle ON
      const inner = (child.fields && child.fields[0]) || {};
      out.push({
        id: `multi-${uid()}`,
        type: "repeater",
        originalType: inner?.type || "text",
        label: child.label,
        standard_field: child.standard_field?._id ? { _id: child.standard_field._id } : null,
        icon: child.standard_field?.icon
          ? `${process.env.REACT_APP_BUCKET_URL}${child.standard_field.icon}`
          : null,
        labelText: child.label,
        displayLabel: null,
        // icon: null,
        required: !!child.required,
        placeholderEnabled: !!(inner && inner.placeholder !== undefined && inner.placeholder !== null && inner.placeholder !== ""),
        placeholder: inner ? (inner.placeholder || "") : "",
        toggle: true,
        nested: true,
        nestedFields: (child.fields || []).map((nf) => ({
          ...nf,
          standard_field:
            typeof nf.standard_field === "string"
              ? nf.standard_field
              : nf.standard_field?._id || null,
        })),

        sourceLibId: null,
        __errors: {},
      });
    } else {
      out.push({
        id: `multi-${uid()}`,
        type: child.type,
        originalType: child.type,
        label: child.label,
        standard_field: child.standard_field?._id ? { _id: child.standard_field._id } : null,
        icon: child.standard_field?.icon
          ? `${process.env.REACT_APP_BUCKET_URL}${child.standard_field.icon}`
          : null,
        labelText: child.label,
        displayLabel: null,
        // icon: null,
        required: !!child.required,
        placeholderEnabled: !!(child.placeholder !== undefined && child.placeholder !== null && child.placeholder !== ""),
        placeholder: child.placeholder || "",
        toggle: false,
        nested: false,
        nestedFields: [],
        sourceLibId: null,
        __errors: {},
      });
    }
  }
  return out;
}

/* ---------------------------
   Helper to find library match
   - Prefer sourceLibId mapping (stored in localStorage)
   - Then prefer exact label match
   - Then prefer same type fallback
----------------------------*/
function bestLibraryMatch(savedLabel = "", savedType = "", library = []) {
  if (!library || library.length === 0) return null;
  const label = (savedLabel || "").toString().trim().toLowerCase();

  const exact = library.find((l) => l.label.trim().toLowerCase() === label);
  if (exact) return exact;

  const contains = library.find((l) => {
    const libLabel = (l.label || "").toLowerCase();
    return libLabel && (label.includes(libLabel) || libLabel.includes(label));
  });
  if (contains) return contains;

  const sameType = library.filter((l) => l.type === savedType);
  if (sameType.length === 1) return sameType[0];
  return sameType[0] || library[0];
}

/* ---------------------------
   Main Component
----------------------------*/
export default function ViewTemplate() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [templateName, setTemplateName] = useState("");
  const [fixedFields, setFixedFields] = useState([]);
  const [multiFields, setMultiFields] = useState([]);
  const [backup, setBackup] = useState({ fixed: [], multi: [] });
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [library, setLibrary] = useState([]);
  const [libLoading, setLibLoading] = useState(false);
  const [libError, setLibError] = useState(null);

  const [expandedSet, setExpandedSet] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [errors, setErrors] = useState({ templateName: null });

  // const { setNodeRef: setFixedNodeRef } = useDroppable({ id: "fixed-area" });
  // const { setNodeRef: setMultiNodeRef } = useDroppable({ id: "multi-area" });
  const isFullLoading = libLoading || loading;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10, // Only drag when moved by 10px
      },
    })
  );
  // load library
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLibLoading(true);
      try {
        const res = await StandardFieldList();
        if (!mounted) return;
        if (res?.status === 1) {
          const list = res.data?.list || [];
          const normalized = list.map((it) => ({
            id: it._id,
            label: it.title,
            type: it.type,
            required: it.required ?? false,
            placeholderEnabled: it.placeholder ?? false,
            icon: it.icon ? `${process.env.REACT_APP_BUCKET_URL}${it.icon}` : ICON_MAP[it.type] || null,
          }));
          setLibrary(normalized);
          setLibError(null);
        } else {
          setLibrary([]);
          setLibError(res?.message || "Failed to load fields");
        }
      } catch (err) {
        setLibError("Failed to load fields");
      } finally {
        if (mounted) setLibLoading(false);
      }
    };
    load();
    return () => (mounted = false);
  }, []);
  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    try {
      const res = await DeleteTemplate(id);
      if (res?.status === 1) {
        showToast("Template deleted successfully", "success");
        navigate("/section");
      } else {
        showToast(res?.message || "Failed to delete template", "error");
      }
    } catch (err) {
      showToast("Failed to delete template", "error");
    } finally {
      setDeleteLoading(false);
      setShowDeletePopup(false);
    }
  };

  // load template after library (so mapping works)
  useEffect(() => {
    let mounted = true;
    const loadTemplate = async () => {
      setLoading(true);
      try {
        const res = await FetchTemplate(id);
        if (!mounted) return;
        if (res?.status === 1 && res.data) {
          const tpl = res.data;
          setTemplateName(tpl.label || "");

          // local mapping stored previously when saving (sourceLibId per index)
          const savedMapRaw = localStorage.getItem(tplMapKey(tpl._id));
          const savedMap = savedMapRaw ? JSON.parse(savedMapRaw) : null;

          // build fixed fields array
          const fixed = [];
          let cardsRepeater = null;
          for (const f of tpl.fields || []) {
            if (f.type === "repeater" && !cardsRepeater) {
              cardsRepeater = f;
            } else {
              const match = bestLibraryMatch(f.label, f.type, library);
              const fixedIndex = fixed.length;
              const libIdFromMap = savedMap?.fixed?.[fixedIndex] || null;
              const libMatchByMap = libIdFromMap ? library.find((l) => l.id === libIdFromMap) : null;
              const useLib = libMatchByMap || match;

              fixed.push({
                id: `fixed-${uid()}`,
                label: f.label,
                standard_field: f.standard_field?._id ? { _id: f.standard_field._id } : null,
                icon: f.standard_field?.icon
                  ? `${process.env.REACT_APP_BUCKET_URL}${f.standard_field.icon}`
                  : useLib?.icon || ICON_MAP[f.type] || null,
                labelText: f.label,
                displayLabel: useLib ? useLib.label : f.label,
                type: f.type,
                originalType: f.type,
                required: !!f.required,
                placeholderEnabled: useLib ? useLib.placeholderEnabled : !!(f.placeholder !== undefined && f.placeholder !== null && f.placeholder !== ""),
                placeholder: f.placeholder || "",
                // icon: useLib ? useLib.icon : ICON_MAP[f.type] || null,
                toggle: false,
                nested: false,
                nestedFields: [],
                sourceLibId: useLib ? useLib.id : null,
                __errors: {},
              });
            }
          }

          // build multi fields (cards) converted
          let multiConverted = [];
          if (cardsRepeater) {
            multiConverted = convertRepeaterToUI(cardsRepeater.fields || [], library).map((it, idx) => {
              const libIdFromMap = savedMap?.multi?.[idx] || null;
              const libMatchByMap = libIdFromMap ? library.find((l) => l.id === libIdFromMap) : null;
              const match = bestLibraryMatch(it.label, it.type, library);
              const useLib = libMatchByMap || match;

              return {
                ...it,
                displayLabel: useLib ? useLib.label : it.displayLabel || it.label,
                standard_field: it.standard_field || null,
                // icon: useLib ? useLib.icon : ICON_MAP[it.type] || null,
                icon: it.standard_field?.icon
                  ? `${process.env.REACT_APP_BUCKET_URL}${it.standard_field.icon}`
                  : useLib?.icon || ICON_MAP[it.type] || null,
                placeholderEnabled: useLib ? useLib.placeholderEnabled : it.placeholderEnabled,
                sourceLibId: useLib ? useLib.id : null,
              };
            });
          } else {
            multiConverted = [];
          }

          // IMPORTANT: set both visible state and backup to the loaded values so undo restores to loaded state
          setFixedFields(fixed);
          setMultiFields(multiConverted);
          setBackup({ fixed: [...fixed], multi: [...multiConverted] });

          setApiError("");
        } else {
          setApiError(res?.message || "Failed to load template");
        }
      } catch (err) {
        setApiError("Failed to load template");
      } finally {
        setLoading(false);
      }
    };

    // wait until library load completes so mapping is accurate
    if (!libLoading) loadTemplate();

    return () => (mounted = false);
  }, [id, libLoading, library]);

  /* ---------------------------
     DND handler
  ----------------------------*/
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;

    // dragging from library -> create new item
    if (active.data.current?.source === "library") {
      const source = active.data.current;
      const newField = {
        id: `${source.id}-${uid()}`,
        type: source.type,
        originalType: source.type,
        label: source.label,
        labelText: "",
        displayLabel: source.label,
        icon: source.icon || ICON_MAP[source.type] || null,
        required: source.required ?? false,
        placeholderEnabled: source.placeholderEnabled ?? false,
        placeholder: source.placeholderEnabled ? "" : null,
        toggle: false,
        nested: false,
        nestedFields: [],
        standard_field: { _id: source.id },
        sourceLibId: source.id,
        __errors: {},
      };

      setBackup({ fixed: fixedFields, multi: multiFields });

      if (over.id === "fixed-area") {
        setFixedFields((p) => [...p, newField]);
        return;
      }
      if (over.id === "multi-area") {
        setMultiFields((p) => [...p, newField]);
        return;
      }

      // if over a specific item -> insert after it
      const fixedIndex = fixedFields.findIndex((f) => f.id === over.id);
      if (fixedIndex !== -1) {
        setFixedFields((prev) => {
          const copy = [...prev];
          copy.splice(fixedIndex + 1, 0, newField);
          return copy;
        });
        return;
      }
      const multiIndex = multiFields.findIndex((f) => f.id === over.id);
      if (multiIndex !== -1) {
        setMultiFields((prev) => {
          const copy = [...prev];
          copy.splice(multiIndex + 1, 0, newField);
          return copy;
        });
        return;
      }
      return;
    }

    // reorder within fixed
    if (fixedFields.some((f) => f.id === active.id)) {
      const oldIndex = fixedFields.findIndex((f) => f.id === active.id);
      const newIndex = fixedFields.findIndex((f) => f.id === over.id);
      if (newIndex !== -1 && oldIndex !== newIndex) {
        setBackup({ fixed: fixedFields, multi: multiFields });
        setFixedFields((p) => arrayMove(p, oldIndex, newIndex));
      }
      return;
    }

    // reorder within multi
    if (multiFields.some((f) => f.id === active.id)) {
      const oldIndex = multiFields.findIndex((f) => f.id === active.id);
      const newIndex = multiFields.findIndex((f) => f.id === over.id);
      if (newIndex !== -1 && oldIndex !== newIndex) {
        setBackup({ fixed: fixedFields, multi: multiFields });
        setMultiFields((p) => arrayMove(p, oldIndex, newIndex));
      }
      return;
    }
  };

  /* ---------------------------
     Expand/collapse, CRUD on fields
  ----------------------------*/
  const toggleExpand = (id) => {
    setExpandedSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleFixedLabelChange = (id, value, key = "labelText") => {
    setFixedFields((p) => p.map((i) => (i.id === id ? {
      ...i, [key]: value, standard_field: i.standard_field?._id
        ? { _id: i.standard_field._id }
        : i.standard_field, 
        __errors: { ...i.__errors, [key]: null }
       }
        : i)));
  };
  const handleMultiLabelChange = (id, value, key = "labelText") => {
    setMultiFields((p) => p.map((i) => (i.id === id ? { ...i, [key]: value,
      standard_field: i.standard_field?._id
        ? { _id: i.standard_field._id }
        : i.standard_field,
         __errors: { ...i.__errors, [key]: null } } : i)));
  };

  const handleFixedDelete = (id) => {
    setBackup({ fixed: fixedFields, multi: multiFields });
    setFixedFields((p) => p.filter((i) => i.id !== id));
    setExpandedSet((s) => {
      const n = new Set(s);
      n.delete(id);
      return n;
    });
  };
  const handleMultiDelete = (id) => {
    setBackup({ fixed: fixedFields, multi: multiFields });
    setMultiFields((p) => p.filter((i) => i.id !== id));
    setExpandedSet((s) => {
      const n = new Set(s);
      n.delete(id);
      return n;
    });
  };

  const handleToggleChangeFixed = (id, checked) => {
    setFixedFields((p) =>
      p.map((i) => {
        if (i.id !== id) return i;
        if (checked) {
          const nestedFields = [{ label: i.labelText || i.label, type: i.originalType || i.type, placeholder: i.placeholder || "" }];
          return { ...i, toggle: true, nested: true, nestedFields, type: "repeater" };
        } else {
          return { ...i, toggle: false, nested: false, nestedFields: [], type: i.originalType || i.type };
        }
      })
    );
  };

  const handleToggleChangeMulti = (id, checked) => {
    setMultiFields((p) =>
      p.map((i) => {
        if (i.id !== id) return i;
        if (checked) {
          const nestedFields = [{ label: i.labelText || i.label, type: i.originalType || i.type, placeholder: i.placeholder || "" }];
          return { ...i, toggle: true, nested: true, nestedFields, type: "repeater" };
        } else {
          return { ...i, toggle: false, nested: false, nestedFields: [], type: i.originalType || i.type };
        }
      })
    );
  };

  const handleUndo = () => {
    setFixedFields(backup.fixed || []);
    setMultiFields(backup.multi || []);
    setExpandedSet(new Set());
  };

  /* ---------------------------
     Validation & Update
  ----------------------------*/
  const validateAll = () => {
    let ok = true;
    const newErrors = { templateName: null };

    if (!templateName || !templateName.trim()) {
      newErrors.templateName = "Template Name is required";
      ok = false;
    }

    const fixed = fixedFields.map((f) => {
      const errs = {};
      if (!f.labelText || !f.labelText.trim()) {
        errs.labelText = "Field label is required";
        ok = false;
      }
      return { ...f, __errors: errs };
    });

    const multi = multiFields.map((f) => {
      const errs = {};
      if (!f.labelText || !f.labelText.trim()) {
        errs.labelText = "Field label is required";
        ok = false;
      }
      return { ...f, __errors: errs };
    });

    setFixedFields(fixed);
    setMultiFields(multi);
    setErrors(newErrors);
    return ok;
  };

  const handleUpdate = async () => {
    setApiError("");
    if (!validateAll()) {
      const toOpen = new Set();
      fixedFields.forEach((f) => {
        if (f.__errors?.labelText) toOpen.add(f.id);
      });
      multiFields.forEach((f) => {
        if (f.__errors?.labelText) toOpen.add(f.id);
      });
      setExpandedSet((prev) => new Set([...Array.from(prev), ...Array.from(toOpen)]));
      return;
    }

    const payload = buildPayload(templateName, fixedFields, multiFields);
    setLoading(true);
    try {
      const res = await UpdateTemplate(id, payload);
      if (res?.status === 1 && res.data) {
        // store local map for icons/labels restoration later (index-based)
        const fixedMap = fixedFields.map((f) => f.sourceLibId || null);
        const multiMap = multiFields.map((m) => m.sourceLibId || null);
        const mapObj = { fixed: fixedMap, multi: multiMap };
        localStorage.setItem(tplMapKey(res.data._id || id), JSON.stringify(mapObj));

        showToast("Template updated successfully", "success");
        navigate("/section");
        return;
      }
      setApiError(res?.message || "Failed to update template");
      showToast(res?.message || "Failed to update template", "error");
    } catch (err) {
      setApiError(err?.message || "Failed to update template");
      showToast(err?.message || "Failed to update template", "error");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------------------
     Render
  ----------------------------*/
  return (
    <div>
      <PageHeader pageTitle="Template Detail">
        <button className="btn-white me-2" onClick={() => navigate("/section")}>
          Discard
        </button>
        <button className="btn-red me-2" onClick={() => setShowDeletePopup(true)}>
          Delete
        </button>
        <button className="btn-black" onClick={handleUpdate} disabled={loading}>
          {loading ? "Updating..." : "Update"}
        </button>

      </PageHeader>

      <div className="page-container">
        <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragEnd={handleDragEnd}>
          <div className="row">
            <div className="col-md-5">
              <div className="theme-card mb-3 p-3">
                <div className="card-title mb-3">Template Details</div>
                <div className="frm-group">
                  <label className="frm-label d-flex align-items-center">Template Name
                    <img
                      className="ms-2"
                      src={infoCircle}
                      alt="Info"
                      title="Template Name"
                    />
                  </label>
                  <input type="text" placeholder="Enter Template Name" className="page-menu-item w-100" value={templateName} onChange={(e) => setTemplateName(e.target.value)} />
                  {errors.templateName && <div className="input-error">{errors.templateName}</div>}
                  {apiError && <div className="input-error mt-2">{apiError}</div>}
                </div>
              </div>

              <div className="theme-card p-3">
                <div className="card-title mb-3">Standard Fields</div>
                {libLoading ? (
                  <div className="center-div">
                    <Spinner size="md" />
                  </div>
                ) : libError ? (
                  <div className="input-error">Failed to load fields</div>
                ) : (
                  <div className="draggable-fields">
                    {library.map((f) => (
                      <DraggableField key={f.id} id={f.id} label={f.label} icon={f.icon} payloadType={f.type} placeholderEnabled={f.placeholderEnabled} />
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="col-md-7">

              {/* SHOW LOADER FIRST */}
              {isFullLoading ? (
                <div className="theme-card p-5 text-center">
                  <div className="center-div">
                    <Spinner size="md" />
                  </div>
                </div>
              ) : (
                <div className="theme-card p-4">

                  <div className="card-title mb-4 d-flex justify-content-between align-items-center">
                    <div>Section Builder</div>

                    {(fixedFields.length > 0 || multiFields.length > 0) && (
                      <button className="undo-btn" onClick={handleUndo}>
                        <img src={undoIcon} alt="undo" /> Undo Changes
                      </button>
                    )}
                  </div>

                  {/* FIXED FIELDS */}
                  <DroppableFixed
                    fields={fixedFields}
                    onLabelChange={handleFixedLabelChange}
                    onDelete={handleFixedDelete}
                    expandedSet={expandedSet}
                    toggleExpand={toggleExpand}
                    onToggleChange={handleToggleChangeFixed}
                  />
                  {/* <div className="mb-3" ref={setFixedNodeRef}>
                    <h6>Fixed Fields</h6>

                    {fixedFields.length === 0 ? (
                      <EmptyBox
                        title="Drop Fixed Fields Here"
                        desc="Add single-instance fields like title or subtitle that appear once per section."
                      />
                    ) : (
                      <SortableContext
                        items={fixedFields.map((f) => f.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {fixedFields.map((it) => (
                          <SortableField
                            key={it.id}
                            item={it}
                            onLabelChange={handleFixedLabelChange}
                            onDelete={handleFixedDelete}
                            isExpanded={expandedSet.has(it.id)}
                            toggleExpand={toggleExpand}
                            onToggleChange={handleToggleChangeFixed}
                          />
                        ))}
                      </SortableContext>
                    )}
                  </div> */}

                  {/* MULTI FIELDS */}
                  <DroppableMulti
                    fields={multiFields}
                    onLabelChange={handleMultiLabelChange}
                    onDelete={handleMultiDelete}
                    expandedSet={expandedSet}
                    toggleExpand={toggleExpand}
                    onToggleChange={handleToggleChangeMulti}
                  />
                  {/* <div ref={setMultiNodeRef} className="drop-box">
                    <h6>Fields for Multiple Instance</h6>

                    {multiFields.length === 0 ? (
                      <EmptyBox
                        title="Drop Fields for Multiple Entries"
                        desc="Add input fields that generate repeatable sets such as testimonials or FAQs."
                      />
                    ) : (
                      <SortableContext
                        items={multiFields.map((f) => f.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {multiFields.map((it) => (
                          <SortableField
                            key={it.id}
                            item={it}
                            onLabelChange={handleMultiLabelChange}
                            onDelete={handleMultiDelete}
                            isExpanded={expandedSet.has(it.id)}
                            toggleExpand={toggleExpand}
                            onToggleChange={handleToggleChangeMulti}
                            isMulti={true}
                          />
                        ))}
                      </SortableContext>
                    )}
                  </div> */}

                </div>
              )}

            </div>

          </div>
        </DndContext>
      </div>
      {showDeletePopup && (
        <div className="popup-overlay">
          <div className="popup-container">
            <h4 className="popup-title">Delete Template?</h4>
            <p className="popup-message">
              Are you sure you want to delete this template? This action cannot be undone.
            </p>

            <div className="popup-actions">
              <button
                className="btn-white"
                onClick={() => setShowDeletePopup(false)}
                disabled={deleteLoading}
              >
                No
              </button>

              <button
                className="btn-red"
                onClick={handleConfirmDelete}
                disabled={deleteLoading}
              >
                {deleteLoading ? "Deleting..." : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
